<template>
  <div class="pt-4">
    <h5 class="mb-5">
      Please upload the masked pictures of the front/back of your credit card
      along with an ID proof
    </h5>
    <validation-observer
      ref="formValidation"
      v-slot="{ handleSubmit, invalid }"
    >
      <b-form
        novalidate
        @submit.stop.prevent="handleSubmit(onSubmit)"
      >
        <BaseFormSelect
          id="selectedCard"
          v-model="details.selectedCard"
          :disabled="disableSelect"
          size="sm"
          data-cy="select-credit-card"
          :options="paymentMethodOptions"
          label="Credit Card to submit KYC Docs for:"
          required
        /> 

        <BaseFileUpload
          id="ccFront"
          v-model="details.ccFront"
          data-cy="file-input-cc-front"
          name="ccFront"
          :accept="acceptedFileTypes"
          :rules="validationRules"
          :lazy="true"
          placeholder="Browse"
          label="Upload card front"
          required
        >
          <template #helpText>
            <b-form-text>
              <div>
                Please block out all but 4 digits of credit card as pictured in
                the example below.
              </div>
              <img
                width="220px"
                src="/img/cc_front.png"
                alt="credit card front"
              >
            </b-form-text>
          </template>
        </BaseFileUpload>

        <BaseFileUpload
          id="ccBack"
          v-model="details.ccBack"
          name="ccBack"
          data-cy="file-input-cc-back"
          :accept="acceptedFileTypes"
          :rules="validationRules"
          :lazy="true"
          placeholder="Browse"
          label="Upload card back"
          required
        >
          <template #helpText>
            <b-form-text class="mt-2">
              <img
                width="220px"
                src="/img/cc_back.png"
                alt="credit card back"
              >
            </b-form-text>
          </template>
        </BaseFileUpload>

        <BaseFileUpload
          id="photoId"
          v-model="details.photoId"
          name="photoId"
          data-cy="file-input-photo-id"
          :accept="acceptedFileTypes"
          :rules="validationRules"
          :lazy="true"
          placeholder="Browse"
          label="Upload ID"
          required
        >
          <template #helpText>
            <b-form-text class="mt-2">
              <img
                width="220px"
                src="/img/drivers_licence.png"
                alt="photo id"
              >
            </b-form-text>
          </template>
        </BaseFileUpload>

        <b-form-text
          v-if="isPreparingFiles"
          data-cy="text-preparing-files"
          class="mb-2"
        >
          Preparing your pictures for upload...
        </b-form-text>

        <div class="text-center">
          <BaseButtonLoader
            id="submit-button"
            data-cy="button-submit"
            :loading="isLoading"
            :disabled="invalid || isPreparingFiles"
            button-class="custom-button--white-hover-grad pulse"
            type="submit"
          >
            Submit
          </BaseButtonLoader>
        </div>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import { showErrorMessage } from '@/notification-utils'
import { needsTranscode, normalizeImageFile } from '@/utils/imageTranscode'

// HEIC/HEIF is what macOS and iOS hand over for camera photos, so it has to be
// selectable even though the API only takes JPEG/PNG - normalizeImageFile
// converts it to a JPEG before submit.
const UPLOAD_FIELDS = ['ccFront', 'ccBack', 'photoId']

export default {
  name: 'KYCForm',
  props: {
    isLoading: {
      type: Boolean,
      default: false
    },
    selectedCard: {
      type: String,
      default: ''
    },
    disableSelect: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      acceptedFileTypes: '.jpg,.png,.jpeg,.heic,.heif',
      // The size cap is deliberately generous: it only guards against absurd
      // input, since anything above the API's 10 MB limit gets downscaled.
      validationRules: 'ext:jpg,jpeg,png,heic,heif|size:51200',
      pendingConversions: 0,
      details: {
        selectedCard: null,
        ccFront: null,
        ccBack: null,
        photoId: null,        
        billingConfigId: null
      }
    }
  },
  computed: {
    ...mapGetters('auth', ['accountDetails']),
    ...mapGetters('billing', ['unverifiedPaymentMethods']),
    isPreparingFiles() {
      return this.pendingConversions > 0
    },
    paymentMethodOptions() {
      return this.unverifiedPaymentMethods.map(pm => {
        return {
          value: pm.id,
          text: `${pm.maskedCC} - ${pm.name}`
        }
      })
    }
  },
  watch: {
    'details.ccFront'(file) {
      this.normalizeUpload('ccFront', file)
    },
    'details.ccBack'(file) {
      this.normalizeUpload('ccBack', file)
    },
    'details.photoId'(file) {
      this.normalizeUpload('photoId', file)
    }
  },
  async created() {    
    this.details.billingConfigId = this.accountDetails.billingConfiguration.id
    await this.fetchPaymentMethods({
      accountId: this.accountDetails.id
    })
    if (this.selectedCard) {
      this.details.selectedCard = this.selectedCard
    } else {
      this.details.selectedCard = this.paymentMethodOptions?.[0]?.value
    }
  },
  methods: {
    ...mapActions('billing', ['submitKYCDocs', 'fetchPaymentMethods']),

    /**
     * Replaces a freshly picked file with a JPEG the API will accept. Files
     * that already qualify are left alone, so this is a no-op for most
     * uploads, and assigning the converted file back does not start another
     * conversion because the result no longer needs one.
     *
     * The uploads are bound with :lazy so BaseFileUpload emits once per pick.
     * Its debounced second emit replays the *original* File, which would
     * otherwise overwrite the conversion a few hundred milliseconds later.
     */
    async normalizeUpload(field, file) {
      if (!file || !needsTranscode(file)) {
        return
      }

      this.pendingConversions += 1

      try {
        const normalized = await normalizeImageFile(file)

        // The user may have picked a different picture while we were busy.
        if (this.details[field] === file) {
          this.details[field] = normalized
        }
      } catch (error) {
        // A failure for a picture the user has already replaced is noise.
        if (this.details[field] === file) {
          this.details[field] = null
          showErrorMessage(error.message)
        }
      } finally {
        this.pendingConversions -= 1
      }
    },

    async onSubmit() {
      if (UPLOAD_FIELDS.some(field => needsTranscode(this.details[field]))) {
        showErrorMessage('Your pictures are still being prepared. Please try again in a moment.')
        return
      }

      this.$emit('submit')
      try {
        await this.submitKYCDocs({
          accountId: this.accountDetails.id,
          paymentMethodId: this.details.selectedCard,
          ccFront: this.details.ccFront,
          ccBack: this.details.ccBack,
          photoId: this.details.photoId,
          billingConfigId: this.details.billingConfigId          
        })
        this.$emit('success')
      } catch (error) {
        this.$emit('error', error.message)
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>
